import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export function pushDataLayerEvent(
  event: string,
  data?: Record<string, unknown>,
) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...data });
}

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "text-color": ["text-fg", "text-text-secondary"],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDuration(seconds?: number): string {
  if (!seconds || !Number.isFinite(seconds)) return "0:00";
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}


export function nullOrNumber(str : string) : number | null {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
}

export function formatLocalDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/** Same as formatLocalDateTime but with seconds, e.g. "2026-08-05 21:21:27". */
export function formatLocalDateTimeWithSeconds(
  value: string | null | undefined,
): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
// Mirrors the backend's email.ValidEmailPattern and 255 char cap.
const EMAIL_PATTERN =
  /^[-!#$%&‘*+/=?^`_.{|}~a-zA-Z0-9]+@([a-zA-Z0-9-]+\.)+[a-zA-Z0-9]{2,}$/;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= 255 && EMAIL_PATTERN.test(trimmed);
}
